import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Typography, Skeleton} from '@mui/material'
import {ChevronRightRounded, FiberManualRecord} from '@mui/icons-material'
import {BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip} from 'recharts'
import {useMobile} from '../../../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../../../utils/helpers'
import {dashboardApi, evvApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {format} from 'date-fns'
import {useNavigate} from 'react-router-dom'
import {SkeletonContainer, TextSkeleton} from '../../../../components'
import {
    ModalActiveServiceAgreements,
    ModalDummyServiceAgreements,
    ModalEndingSoonServiceAgreements,
    ModalExpiredServiceAgreements,
    ModalOverServices,
    ModalActiveClients,
    ModalIneligibleClients,
    ModalNewClients,
    ModalActiveCaregivers,
    ModalPendingCaregivers,
    ModalSuccessfulHhaxSync,
    ModalPendingHhaxSync,
    ModalRejectedHhaxSync,
} from '../../components'

export default () => {
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()
    const [data, setData] = useState(null)
    const [hhaxSummary, setHhaxSummary] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const [hhaxSkeleton, setHhaxSkeleton] = useState(true)
    const requestId = useRef(0)
    const navigate = useNavigate()
    const [openModalActiveServiceAgreements, setOpenModalActiveServiceAgreements] = useState(false)
    const [openModalDummyServiceAgreements, setOpenModalDummyServiceAgreements] = useState(false)
    const [openModalEndingSoonServiceAgreements, setOpenModalEndingSoonServiceAgreements] = useState(false)
    const [openModalExpiredServiceAgreements, setOpenModalExpiredServiceAgreements] = useState(false)
    const [openModalOverServices, setOpenModalOverServices] = useState(false)
    const [openModalActiveClients, setOpenModalActiveClients] = useState(false)
    const [openModalIneligibleClients, setOpenModalIneligibleClients] = useState(false)
    const [openModalNewClients, setOpenModalNewClients] = useState(false)
    const [openModalActiveCaregivers, setOpenModalActiveCaregivers] = useState(false)
    const [openModalPendingCaregivers, setOpenModalPendingCaregivers] = useState(false)
    const [openModalSuccessfulHhaxSync, setOpenModalSuccessfulHhaxSync] = useState(false)
    const [openModalPendingHhaxSync, setOpenModalPendingHhaxSync] = useState(false)
    const [openModalRejectedHhaxSync, setOpenModalRejectedHhaxSync] = useState(false)

    // A new day, branch or Refresh replaces the figures with the skeleton.
    useEffect(() => {
        setSkeleton(true)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getOrganizationAtGlance(branchId, {date: format(dashboardDate, 'yyyy-MM-dd')})
                    .then(({data: {data}}) => {
                        if (id !== requestId.current) return
                        setData(data)
                        setSkeleton(false)
                    })
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        setHhaxSkeleton(true)
        evvApi
            .getSummary()
            .then(({data}) => {
                setHhaxSummary(data)
                setHhaxSkeleton(false)
            })
            .catch(error => {
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                setHhaxSkeleton(false)
            })
    }, [dashboardRefresh])

    // The number the card leads with has to be the number the bar underneath
    // explains. counts.all is every visit the EVV screens classify — submitted,
    // withdrawn and not_required included — none of which have a segment here,
    // so the card led with 12 over a bar adding up to 7. Sum what is shown.
    const hhaxShown = ['approved', 'pending_review', 'rejected', 'blocked'].reduce((n, k) => n + (hhaxSummary?.counts?.[k] || 0), 0)

    const panels = [
        {
            for: "Service Agreement's Overview",
            legend: [
                {key: 'Active', label: 'Active', action: 'ModalActiveServiceAgreements'},
                {key: 'Dummy', label: 'Dummy', action: 'ModalDummyServiceAgreements'},
                {key: 'EndingSoon', label: 'Ending Soon', action: 'ModalEndingSoonServiceAgreements'},
                {key: 'Expired', label: 'Expired', action: 'ModalExpiredServiceAgreements'},
                {key: 'OverServices', label: 'Over-services', action: 'ModalOverServices'},
            ],
            colors: {
                Active: '#22c55e',
                Dummy: '#eab308',
                EndingSoon: '#f97316',
                Expired: '#ef4444',
                OverServices: '#9ca3af',
            },
            data: [
                {
                    Active: data?.service_agreements?.active || 0,
                    Dummy: data?.service_agreements?.expiring || 0,
                    EndingSoon: data?.service_agreements?.ending_soon || 0,
                    Expired: data?.service_agreements?.expired || 0,
                    OverServices: data?.service_agreements?.over_services || 0,
                },
            ],
        },
        {
            // Blocked is one of the four outcomes the API's total counts, so
            // leaving it out of the bar made the card contradict itself — 9
            // visits over a bar adding up to 4. It is also the one an agency
            // most needs to see: a blocked visit is legally required to reach
            // the state and cannot get there until a payer acts.
            for: 'HHAX Sync',
            legend: [
                {key: 'Approved', label: 'Approved', action: 'ModalSuccessfulHhaxSync'},
                {key: 'Pending', label: 'Pending', action: 'ModalPendingHhaxSync'},
                {key: 'Rejected', label: 'Rejected', action: 'ModalRejectedHhaxSync'},
                {key: 'Blocked', label: 'Blocked', action: 'EvvBlocked'},
            ],
            colors: {
                Approved: '#22c55e',
                Pending: '#eab308',
                Rejected: '#ef4444',
                Blocked: '#6b7280',
            },
            data: [
                {
                    Approved: hhaxSummary?.counts?.approved || 0,
                    Pending: hhaxSummary?.counts?.pending_review || 0,
                    Rejected: hhaxSummary?.counts?.rejected || 0,
                    Blocked: hhaxSummary?.counts?.blocked || 0,
                },
            ],
        },
        {
            for: 'Clients',
            legend: [
                {key: 'Active', label: 'Active', action: 'ModalActiveClients'},
                {key: 'Ineligible', label: 'Ineligible', action: 'ModalIneligibleClients'},
                {key: 'New', label: 'New', action: 'ModalNewClients'},
            ],
            colors: {
                Active: '#22c55e',
                Ineligible: '#ef4444',
                New: '#9ca3af',
            },
            data: [{Active: data?.clients?.active || 0, Ineligible: data?.clients?.ineligible || 0, New: data?.clients?.new || 0}],
        },
        {
            for: 'Caregivers',
            legend: [
                {key: 'Active', label: 'Active', action: 'ModalActiveCaregivers'},
                {key: 'Pending', label: 'Pending', action: 'ModalPendingCaregivers'},
            ],
            colors: {
                Active: '#22c55e',
                Pending: '#eab308',
            },
            data: [{Active: data?.caregivers?.active || 0, Pending: data?.caregivers?.pending || 0}],
        },
    ]

    const openModal = action => {
        switch (action) {
            case 'ModalActiveServiceAgreements':
                setOpenModalActiveServiceAgreements(true)
                break
            case 'ModalDummyServiceAgreements':
                setOpenModalDummyServiceAgreements(true)
                break
            case 'ModalEndingSoonServiceAgreements':
                setOpenModalEndingSoonServiceAgreements(true)
                break
            case 'ModalExpiredServiceAgreements':
                setOpenModalExpiredServiceAgreements(true)
                break
            case 'ModalOverServices':
                setOpenModalOverServices(true)
                break
            case 'ModalActiveClients':
                setOpenModalActiveClients(true)
                break
            case 'ModalIneligibleClients':
                setOpenModalIneligibleClients(true)
                break
            case 'ModalNewClients':
                setOpenModalNewClients(true)
                break
            case 'ModalActiveCaregivers':
                setOpenModalActiveCaregivers(true)
                break
            case 'ModalPendingCaregivers':
                setOpenModalPendingCaregivers(true)
                break
            case 'ModalSuccessfulHhaxSync':
                setOpenModalSuccessfulHhaxSync(true)
                break
            case 'ModalPendingHhaxSync':
                setOpenModalPendingHhaxSync(true)
                break
            case 'ModalRejectedHhaxSync':
                setOpenModalRejectedHhaxSync(true)
                break
            case 'EvvBlocked':
                navigate('/evv?status=blocked')
                break
        }
    }

    return (
        <>
            <div className="container">
                <div className="card1">
                    <Typography variant="h6" color="text.primary">
                        Service Agreement's Overview
                    </Typography>
                    <br />
                    <br />
                    <div className="description">
                        {skeleton ? (
                            <TextSkeleton width={40} fontSize={20} />
                        ) : (
                            <Typography variant="h6" color="text.primary">
                                {data?.service_agreements?.total || 0}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            total authorizations
                        </Typography>
                    </div>
                    <br />
                    {skeleton ? (
                        <SkeletonContainer>
                            <Skeleton variant="rounded" height={14} sx={{borderRadius: 1}} />
                        </SkeletonContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={14}>
                            <BarChart
                                data={panels[0].data}
                                layout="vertical"
                                barSize={14}
                                margin={{top: 0, right: 0, bottom: 0, left: 0}}
                                stackOffset="expand">
                                <XAxis type="number" hide />
                                <YAxis type="category" hide />
                                <Tooltip
                                    labelFormatter={() => ''}
                                    formatter={(value, name) => {
                                        const label = panels[0].legend.find(l => l.key === name)?.label || name
                                        const total = panels[0].legend.reduce((sum, l) => sum + (panels[0].data[0][l.key] || 0), 0)
                                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                                        return [`${value} (${pct}%)`, label]
                                    }}
                                    contentStyle={{fontSize: 12}}
                                />
                                {panels[0].legend.map((item, i) => (
                                    <Bar
                                        key={item.key}
                                        dataKey={item.key}
                                        stackId="a"
                                        fill={panels[0].colors[item.key]}
                                        radius={
                                            i === 0 && panels[0].legend.length === 1
                                                ? [4, 4, 4, 4]
                                                : i === 0
                                                  ? [4, 0, 0, 4]
                                                  : i === panels[0].legend.length - 1
                                                    ? [0, 4, 4, 0]
                                                    : 0
                                        }
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                    <br />
                    <div className="legend-container">
                        {panels[0].legend.map((item, index) => (
                            <div className="legend" key={index} onClick={() => openModal(item?.action)}>
                                <div className="legend-top">
                                    <FiberManualRecord fontSize="small" style={{color: panels[0]?.colors?.[item?.key]}} />
                                    {skeleton ? (
                                        <TextSkeleton width={24} fontSize={20} />
                                    ) : (
                                        <Typography variant="h6" color="text.primary">
                                            {panels[0]?.data?.[0]?.[item?.key]}
                                        </Typography>
                                    )}
                                </div>
                                <Typography variant="body2" color="text.secondary">
                                    {item?.label}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="card2">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            HHAX Sync
                        </Typography>
                        {!mobile && (
                            <div className="view-more" onClick={() => navigate('/evv')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        )}
                    </div>
                    <br />
                    <br />
                    <div className="description">
                        {hhaxSkeleton ? (
                            <TextSkeleton width={40} fontSize={20} />
                        ) : (
                            <Typography variant="h6" color="text.primary">
                                {hhaxShown}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            visit confirmations
                        </Typography>
                    </div>
                    <br />
                    {hhaxSkeleton ? (
                        <SkeletonContainer>
                            <Skeleton variant="rounded" height={14} sx={{borderRadius: 1}} />
                        </SkeletonContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={14}>
                            <BarChart
                                data={panels[1].data}
                                layout="vertical"
                                barSize={14}
                                margin={{top: 0, right: 0, bottom: 0, left: 0}}
                                stackOffset="expand">
                                <XAxis type="number" hide />
                                <YAxis type="category" hide />
                                <Tooltip
                                    labelFormatter={() => ''}
                                    formatter={(value, name) => {
                                        const label = panels[1].legend.find(l => l.key === name)?.label || name
                                        const total = panels[1].legend.reduce((sum, l) => sum + (panels[1].data[0][l.key] || 0), 0)
                                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                                        return [`${value} (${pct}%)`, label]
                                    }}
                                    contentStyle={{fontSize: 12}}
                                />
                                {panels[1].legend.map((item, i) => (
                                    <Bar
                                        key={item.key}
                                        dataKey={item.key}
                                        stackId="a"
                                        fill={panels[1].colors[item.key]}
                                        radius={
                                            i === 0 && panels[1].legend.length === 1
                                                ? [4, 4, 4, 4]
                                                : i === 0
                                                  ? [4, 0, 0, 4]
                                                  : i === panels[1].legend.length - 1
                                                    ? [0, 4, 4, 0]
                                                    : 0
                                        }
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                    <br />
                    <div className="legend-container">
                        {panels[1].legend.map((item, index) => (
                            <div className="legend" key={index} onClick={() => openModal(item?.action)}>
                                <div className="legend-top">
                                    <FiberManualRecord fontSize="small" style={{color: panels[1]?.colors?.[item?.key]}} />
                                    {hhaxSkeleton ? (
                                        <TextSkeleton width={24} fontSize={20} />
                                    ) : (
                                        <Typography variant="h6" color="text.primary">
                                            {panels[1]?.data?.[0]?.[item?.key]}
                                        </Typography>
                                    )}
                                </div>
                                <Typography variant="body2" color="text.secondary">
                                    {item?.label}
                                </Typography>
                            </div>
                        ))}
                    </div>
                    <br />
                    {mobile && (
                        <div className="view-more-container">
                            <div className="view-more" onClick={() => navigate('/evv')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <br />
            <div className="container">
                <div className="card1">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            Clients
                        </Typography>
                        {!mobile && (
                            <div className="view-more" onClick={() => navigate('/clients-management')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        )}
                    </div>
                    <br />
                    <br />
                    <div className="description">
                        {skeleton ? (
                            <TextSkeleton width={40} fontSize={20} />
                        ) : (
                            <Typography variant="h6" color="text.primary">
                                {data?.clients?.total || 0}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            on the books
                        </Typography>
                    </div>
                    <br />
                    {skeleton ? (
                        <SkeletonContainer>
                            <Skeleton variant="rounded" height={14} sx={{borderRadius: 1}} />
                        </SkeletonContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={14}>
                            <BarChart
                                data={panels[2].data}
                                layout="vertical"
                                barSize={14}
                                margin={{top: 0, right: 0, bottom: 0, left: 0}}
                                stackOffset="expand">
                                <XAxis type="number" hide />
                                <YAxis type="category" hide />
                                <Tooltip
                                    labelFormatter={() => ''}
                                    formatter={(value, name) => {
                                        const label = panels[2].legend.find(l => l.key === name)?.label || name
                                        const total = panels[2].legend.reduce((sum, l) => sum + (panels[2].data[0][l.key] || 0), 0)
                                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                                        return [`${value} (${pct}%)`, label]
                                    }}
                                    contentStyle={{fontSize: 12}}
                                />
                                {panels[2].legend.map((item, i) => (
                                    <Bar
                                        key={item.key}
                                        dataKey={item.key}
                                        stackId="a"
                                        fill={panels[2].colors[item.key]}
                                        radius={
                                            i === 0 && panels[2].legend.length === 1
                                                ? [4, 4, 4, 4]
                                                : i === 0
                                                  ? [4, 0, 0, 4]
                                                  : i === panels[2].legend.length - 1
                                                    ? [0, 4, 4, 0]
                                                    : 0
                                        }
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                    <br />
                    <div className="legend-container">
                        {panels[2].legend.map((item, index) => (
                            <div className="legend" key={index} onClick={() => openModal(item?.action)}>
                                <div className="legend-top">
                                    <FiberManualRecord fontSize="small" style={{color: panels[2]?.colors?.[item?.key]}} />
                                    {skeleton ? (
                                        <TextSkeleton width={24} fontSize={20} />
                                    ) : (
                                        <Typography variant="h6" color="text.primary">
                                            {panels[2]?.data?.[0]?.[item?.key]}
                                        </Typography>
                                    )}
                                </div>
                                <Typography variant="body2" color="text.secondary">
                                    {item?.label}
                                </Typography>
                            </div>
                        ))}
                    </div>
                    <br />
                    {mobile && (
                        <div className="view-more-container">
                            <div className="view-more" onClick={() => navigate('/clients-management')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        </div>
                    )}
                </div>
                <div className="card2">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            Caregivers
                        </Typography>
                        {!mobile && (
                            <div className="view-more" onClick={() => navigate('/caregivers-management')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        )}
                    </div>
                    <br />
                    <br />
                    <div className="description">
                        {skeleton ? (
                            <TextSkeleton width={40} fontSize={20} />
                        ) : (
                            <Typography variant="h6" color="text.primary">
                                {data?.caregivers?.total || 0}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            in the network
                        </Typography>
                    </div>
                    <br />
                    {skeleton ? (
                        <SkeletonContainer>
                            <Skeleton variant="rounded" height={14} sx={{borderRadius: 1}} />
                        </SkeletonContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={14}>
                            <BarChart
                                data={panels[3].data}
                                layout="vertical"
                                barSize={14}
                                margin={{top: 0, right: 0, bottom: 0, left: 0}}
                                stackOffset="expand">
                                <XAxis type="number" hide />
                                <YAxis type="category" hide />
                                <Tooltip
                                    labelFormatter={() => ''}
                                    formatter={(value, name) => {
                                        const label = panels[3].legend.find(l => l.key === name)?.label || name
                                        const total = panels[3].legend.reduce((sum, l) => sum + (panels[3].data[0][l.key] || 0), 0)
                                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                                        return [`${value} (${pct}%)`, label]
                                    }}
                                    contentStyle={{fontSize: 12}}
                                />
                                {panels[3].legend.map((item, i) => (
                                    <Bar
                                        key={item.key}
                                        dataKey={item.key}
                                        stackId="a"
                                        fill={panels[3].colors[item.key]}
                                        radius={
                                            i === 0 && panels[3].legend.length === 1
                                                ? [4, 4, 4, 4]
                                                : i === 0
                                                  ? [4, 0, 0, 4]
                                                  : i === panels[3].legend.length - 1
                                                    ? [0, 4, 4, 0]
                                                    : 0
                                        }
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                    <br />
                    <div className="legend-container">
                        {panels[3].legend.map((item, index) => (
                            <div className="legend" key={index} onClick={() => openModal(item?.action)}>
                                <div className="legend-top">
                                    <FiberManualRecord fontSize="small" style={{color: panels[3]?.colors?.[item?.key]}} />
                                    {skeleton ? (
                                        <TextSkeleton width={24} fontSize={20} />
                                    ) : (
                                        <Typography variant="h6" color="text.primary">
                                            {panels[3]?.data?.[0]?.[item?.key]}
                                        </Typography>
                                    )}
                                </div>
                                <Typography variant="body2" color="text.secondary">
                                    {item?.label}
                                </Typography>
                            </div>
                        ))}
                    </div>
                    <br />
                    {mobile && (
                        <div className="view-more-container">
                            <div className="view-more" onClick={() => navigate('/caregivers-management')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                                <ChevronRightRounded fontSize="medium" color="primary" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ModalActiveServiceAgreements open={openModalActiveServiceAgreements} setOpen={setOpenModalActiveServiceAgreements} />
            <ModalDummyServiceAgreements open={openModalDummyServiceAgreements} setOpen={setOpenModalDummyServiceAgreements} />
            <ModalEndingSoonServiceAgreements open={openModalEndingSoonServiceAgreements} setOpen={setOpenModalEndingSoonServiceAgreements} />
            <ModalExpiredServiceAgreements open={openModalExpiredServiceAgreements} setOpen={setOpenModalExpiredServiceAgreements} />
            <ModalOverServices open={openModalOverServices} setOpen={setOpenModalOverServices} />
            <ModalActiveClients open={openModalActiveClients} setOpen={setOpenModalActiveClients} />
            <ModalIneligibleClients open={openModalIneligibleClients} setOpen={setOpenModalIneligibleClients} />
            <ModalNewClients open={openModalNewClients} setOpen={setOpenModalNewClients} />
            <ModalActiveCaregivers open={openModalActiveCaregivers} setOpen={setOpenModalActiveCaregivers} />
            <ModalPendingCaregivers open={openModalPendingCaregivers} setOpen={setOpenModalPendingCaregivers} />
            <ModalSuccessfulHhaxSync open={openModalSuccessfulHhaxSync} setOpen={setOpenModalSuccessfulHhaxSync} />
            <ModalPendingHhaxSync open={openModalPendingHhaxSync} setOpen={setOpenModalPendingHhaxSync} />
            <ModalRejectedHhaxSync open={openModalRejectedHhaxSync} setOpen={setOpenModalRejectedHhaxSync} />
        </>
    )
}
